import { colorPoolForLevel, intervalToSpawnRate, spawnRateToInterval, timing, variantForLevel, } from './params';
/** Petit LCG pour déterminisme tests. */
function lcg(seed) {
    let s = seed >>> 0;
    if (s === 0)
        s = 1;
    return () => {
        s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
        return s / 4294967296;
    };
}
/** Calcule le schedule de spawns à partir du seed + intervalleMs. */
export function buildSpawnSchedule(seed, trialIdx, intervalMs, durationMs, colorPool, nTracks) {
    const rand = lcg(seed * 7919 + trialIdx + 1);
    const events = [];
    // Premier spawn vers ~intervalMs/3 pour laisser respirer l'utilisateur.
    let cursor = Math.floor(intervalMs / 3);
    while (cursor < durationMs) {
        // Jitter ±25% pour éviter une grille parfaite.
        const jitter = (rand() - 0.5) * 0.5 * intervalMs;
        const at = Math.max(0, Math.min(durationMs - 1, Math.floor(cursor + jitter)));
        const colorIdx = Math.floor(rand() * colorPool.length);
        const trackIdx = Math.floor(rand() * nTracks);
        events.push({
            atMs: at,
            color: colorPool[colorIdx],
            initialTrack: trackIdx,
        });
        cursor += intervalMs;
    }
    return events;
}
export function makeStimulus(trialIdx, intervalMs, level, seed) {
    const variant = variantForLevel(level);
    const colorPool = colorPoolForLevel(level);
    return {
        trialIdx,
        spawnRate: intervalToSpawnRate(intervalMs),
        durationMs: timing.trialDurationMs,
        trainColors: colorPool,
        nTracks: variant.nTracks,
        seed,
    };
}
export function createTrainOfThoughtAdapter(level = 1) {
    const initialVariant = variantForLevel(level);
    let state = {
        subPhase: 'idle',
        stim: null,
        feedback: null,
        level,
        variant: initialVariant,
        trains: [],
        switchStates: Array.from({ length: initialVariant.nTracks }, (_, i) => i),
        elapsedMs: 0,
        trainsRouted: 0,
        trainsCorrect: 0,
        collisions: 0,
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    let firstTapAt = null;
    let pendingSchedule = [];
    let nextSpawnIdx = 0;
    let nextTrainId = 0;
    const timers = [];
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    /**
     * À chaque tick durant playing : spawn nouveaux trains dus, fait avancer
     * les trains existants, comptabilise arrivées + collisions.
     */
    function tick(now) {
        if (state.subPhase !== 'playing' || !state.stim)
            return;
        const elapsed = now - trialStart;
        // Spawn new trains.
        while (nextSpawnIdx < pendingSchedule.length &&
            pendingSchedule[nextSpawnIdx].atMs <= elapsed) {
            const spawn = pendingSchedule[nextSpawnIdx];
            const newTrain = {
                id: nextTrainId++,
                spawn,
                currentTrack: spawn.initialTrack,
                arrived: false,
            };
            state = {
                ...state,
                trains: [...state.trains, newTrain],
                trainsRouted: state.trainsRouted + 1,
            };
            nextSpawnIdx += 1;
        }
        // Avance trains : arrivée déterministe au bout de TRAVEL_MS (durée fixe
        // simple pour MVP). Le train "arrive" et est routé selon switchStates.
        const TRAVEL_MS = 3000;
        let trainsCorrectDelta = 0;
        let collisionsDelta = 0;
        const updatedTrains = [];
        for (const tr of state.trains) {
            if (tr.arrived) {
                updatedTrains.push(tr);
                continue;
            }
            const ageMs = elapsed - tr.spawn.atMs;
            if (ageMs >= TRAVEL_MS) {
                // Train arrive. Voie d'arrivée = switchStates[currentTrack] mappé
                // (MVP simple : la voie reste celle où il a été aiguillé).
                const finalTrack = state.switchStates[tr.currentTrack] ?? tr.currentTrack;
                const stationColor = state.stim.trainColors[finalTrack % state.stim.trainColors.length];
                const correct = tr.spawn.color === stationColor;
                if (correct)
                    trainsCorrectDelta += 1;
                else
                    collisionsDelta += 1;
                updatedTrains.push({ ...tr, arrived: true, arrivedTrack: finalTrack });
            }
            else {
                updatedTrains.push(tr);
            }
        }
        state = {
            ...state,
            trains: updatedTrains,
            elapsedMs: elapsed,
            trainsCorrect: state.trainsCorrect + trainsCorrectDelta,
            collisions: state.collisions + collisionsDelta,
        };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function startTickLoop() {
        const TICK_MS = 50;
        const loop = () => {
            if (state.subPhase !== 'playing')
                return;
            tick(Date.now());
            timers.push(setTimeout(loop, TICK_MS));
        };
        timers.push(setTimeout(loop, TICK_MS));
    }
    function resolvePromise() {
        if (!resolver)
            return;
        const collisions = state.collisions;
        const success = collisions === 0;
        const resp = {
            trainsRouted: state.trainsRouted,
            trainsCorrect: state.trainsCorrect,
            collisions,
            success,
            reactionTimeMs: firstTapAt !== null ? firstTapAt - trialStart : 0,
        };
        const r = resolver;
        resolver = null;
        rejecter = null;
        update({ subPhase: 'awaiting_resolution' });
        r(resp);
    }
    return {
        mount() {
            // Idempotent.
        },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                firstTapAt = null;
                const intervalMs = spawnRateToInterval(stim.spawnRate);
                pendingSchedule = buildSpawnSchedule(stim.seed, stim.trialIdx, intervalMs, stim.durationMs, stim.trainColors, stim.nTracks);
                nextSpawnIdx = 0;
                nextTrainId = 0;
                update({
                    subPhase: 'fixation',
                    stim,
                    feedback: null,
                    trains: [],
                    switchStates: Array.from({ length: stim.nTracks }, (_, i) => i),
                    elapsedMs: 0,
                    trainsRouted: 0,
                    trainsCorrect: 0,
                    collisions: 0,
                });
                timers.push(setTimeout(() => {
                    update({ subPhase: 'playing' });
                    trialStart = Date.now();
                    startTickLoop();
                    // Auto-fin après durationMs.
                    timers.push(setTimeout(() => {
                        if (state.subPhase === 'playing') {
                            resolvePromise();
                        }
                    }, stim.durationMs));
                }, timing.fixationMs));
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, timing.feedbackMs));
            });
        },
        pause() {
            // Sprint 0.5 : no-op.
        },
        resume() {
            // Sprint 0.5 : no-op.
        },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        handleSwitchTap(switchIdx) {
            if (state.subPhase !== 'playing')
                return;
            if (switchIdx < 0 || switchIdx >= state.switchStates.length)
                return;
            if (firstTapAt === null)
                firstTapAt = Date.now();
            // Flip : route vers la voie suivante (rotation simple sur nTracks).
            const nTracks = state.switchStates.length;
            const newSwitches = [...state.switchStates];
            newSwitches[switchIdx] = (newSwitches[switchIdx] + 1) % nTracks;
            // Trains qui sont en route sur cet aiguillage : on met à jour
            // currentTrack pour les trains pas encore arrivés (MVP simple).
            const updatedTrains = state.trains.map((tr) => {
                if (tr.arrived)
                    return tr;
                if (tr.currentTrack !== switchIdx)
                    return tr;
                return { ...tr, currentTrack: newSwitches[switchIdx] };
            });
            update({ switchStates: newSwitches, trains: updatedTrains });
        },
        endTrial() {
            if (state.subPhase !== 'playing')
                return;
            resolvePromise();
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({
                level: newLevel,
                variant: v,
                switchStates: Array.from({ length: v.nTracks }, (_, i) => i),
            });
        },
    };
}
//# sourceMappingURL=adapter.js.map